import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router';
import { SessionProvider } from './lib/session.js';
import { App } from './App.js';
import './styles/app.css';

/* ============================================================================
   The client portal's entry point.
   ----------------------------------------------------------------------------
   The theme bootstrap is NOT imported here. It is inlined into <head> as a
   blocking script by a plugin in vite.config.ts, because anything imported
   from this module runs after the first paint — at which point the page has
   already rendered light and has to flip.
   ========================================================================== */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /* A client checking on their site is not watching a dashboard; they open
         it, read it and close it. Refetching on every window focus would spend
         their data on an answer that has not changed. */
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      /* One retry, not three. On bad 4G a failing request usually keeps
         failing, and three rounds of backoff is twenty seconds of a spinner
         before anyone is told anything. */
      retry: 1,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          <App />
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
